import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChartBarIcon, 
  TableCellsIcon, 
  DocumentTextIcon,
  ShoppingBagIcon,
  PresentationChartBarIcon,
  ArrowPathIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowUpTrayIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children, onRefresh, isLoading }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  // Determinar a view atual baseada na URL
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return 'dashboard';
    if (path === '/orders') return 'orders';
    if (path === '/users') return 'users';
    if (path === '/company-report') return 'company-report';
    if (path === '/products') return 'products';
    if (path === '/statistics') return 'statistics';
    return 'dashboard';
  };

  const currentView = getCurrentView();

  // Verificar se o usuário pode acessar a página de gerenciamento
  const canAccessUsers = () => {
    if (!user) return false;
    const role = user.role || user.data?.role;
    const isAdmin = role === 'admin' || role === 'master' || (Array.isArray(user.permissions) && user.permissions.includes('admin'));
    
    // Considerar também o override local se existir
    const identifier = (user.email || user.username || user.name || '').toLowerCase();
    const isOverride = identifier === 'teste@stok.com' || identifier === 'master@siger.com' || identifier === 'antunes@mupa.app';
    
    return isAdmin || isOverride;
  };

  // Verificar se está em uma página da empresa
  const isCompanyPage = () => {
    return ['company-report', 'products', 'statistics'].includes(currentView);
  };

  // Filtrar navegação baseada na página atual e permissões
  const getFilteredNavigation = () => {
    let filtered = navigation;
    
    if (isCompanyPage()) {
      filtered = filtered.filter(item => item.category === 'company');
    } else {
      filtered = filtered.filter(item => item.category === 'main');
    }

    // Filtrar 'Gerenciar Usuários' se não tiver permissão
    if (!canAccessUsers()) {
      filtered = filtered.filter(item => item.key !== 'users');
    }

    return filtered;
  };

  const navigation = [
    {
      name: 'Dashboard',
      key: 'dashboard',
      icon: ChartBarIcon,
      description: 'Visão geral e análises',
      category: 'main'
    },
    {
      name: 'Lista de Ordens',
      key: 'orders',
      icon: TableCellsIcon,
      description: 'Tabela detalhada com filtros',
      category: 'main'
    },
    {
      name: 'Gerenciar Usuários',
      key: 'users',
      icon: UserGroupIcon,
      description: 'Administração de acessos',
      category: 'main'
    },
    {
      name: 'Relatório da Empresa',
      key: 'company-report',
      icon: DocumentTextIcon,
      description: 'Análise específica da empresa',
      category: 'company'
    },
    {
      name: 'Lista de Produtos',
      key: 'products',
      icon: ShoppingBagIcon,
      description: 'Produtos da empresa',
      category: 'company'
    },
    {
      name: 'Estatísticas',
      key: 'statistics',
      icon: PresentationChartBarIcon,
      description: 'Análise detalhada',
      category: 'company'
    }
  ];

  return (
    <div className="h-screen bg-dark-bg flex">
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-dark-card border-r border-gray-700 transform transition-transform duration-300 ease-in-out lg:relative lg:transform-none lg:flex-shrink-0 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
                     {/* Header */}
           <div className="flex items-center justify-between p-6 border-b border-gray-700">
             <div>
               <h1 className="text-xl font-bold text-dark-text">PGS-IT</h1>
               <p className="text-sm text-dark-text-secondary">
                 {isCompanyPage() ? 'Análise da Empresa' : 'Ordens de Serviço'}
               </p>
               {isCompanyPage() && (
                 <div className="mt-1">
                   <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30">
                     Empresa 1728965891007x215886838679286700
                   </span>
                 </div>
               )}
             </div>
             <button
               onClick={() => setIsSidebarOpen(false)}
               className="lg:hidden text-dark-text-secondary hover:text-dark-text"
             >
               <XMarkIcon className="w-6 h-6" />
             </button>
           </div>

                     {/* Navigation */}
           <nav className="flex-1 p-4 space-y-2">
             {/* Navegação da empresa */}
             {getFilteredNavigation().map((item) => {
               const Icon = item.icon;
               const isActive = currentView === item.key;
               
               return (
                 <button
                   key={item.key}
                   onClick={() => {
                     navigate(item.key === 'dashboard' ? '/' : `/${item.key === 'users' ? 'users' : item.key}`);
                     setIsSidebarOpen(false);
                   }}
                   className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                     isActive
                       ? 'bg-primary text-white shadow-lg'
                       : 'text-dark-text-secondary hover:bg-gray-700 hover:text-dark-text'
                   }`}
                 >
                   <Icon className="w-6 h-6 mt-0.5 flex-shrink-0" />
                   <div>
                     <div className={`font-medium ${isActive ? 'text-white' : ''}`}>
                       {item.name}
                     </div>
                     <div className={`text-sm ${isActive ? 'text-blue-100' : 'text-dark-text-secondary'}`}>
                       {item.description}
                     </div>
                   </div>
                 </button>
               );
             })}
           </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-700">
            {isAuthenticated && user && (
              <div className="flex flex-col gap-1 px-2 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold text-xs uppercase">
                    {(user.name || user.username || user.email || 'U').charAt(0)}
                  </div>
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-medium text-dark-text truncate">
                      {user.name || user.username || user.email || 'Usuário'}
                    </span>
                    <span className="text-xs text-dark-text-secondary truncate">
                      {user.email}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
                 {/* Top bar */}
         <div className="bg-dark-card border-b border-gray-700 px-4 py-3 flex items-center justify-between lg:px-6 flex-shrink-0">
           <div className="flex items-center gap-4">
             <button
               onClick={() => setIsSidebarOpen(true)}
               className="lg:hidden text-dark-text-secondary hover:text-dark-text"
             >
               <Bars3Icon className="w-6 h-6" />
             </button>
             <div>
               <h2 className="text-lg font-semibold text-dark-text">
                 {navigation.find(item => item.key === currentView)?.name}
               </h2>
               <p className="text-sm text-dark-text-secondary">
                 {navigation.find(item => item.key === currentView)?.description}
               </p>
             </div>
           </div>
           
                       {/* Navegação rápida da empresa */}
            {isCompanyPage() && (
              <div className="hidden md:flex items-center gap-2">
                <span className="text-xs text-dark-text-secondary mr-2">Navegação:</span>
                {getFilteredNavigation().map((item) => {
                  const isActive = currentView === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => navigate(`/${item.key}`)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-dark-text-secondary hover:text-dark-text hover:bg-gray-700'
                      }`}
                    >
                      {item.name}
                    </button>
                  );
                })}
              </div>
            )}
           
         {/* Status indicator */}
           <div className="flex items-center gap-3">
             {isLoading && (
               <div className="flex items-center gap-2 text-dark-text-secondary">
                 <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                 <span className="text-sm">Carregando...</span>
               </div>
             )}
             {/* User & Logout */}
             {isAuthenticated && (
               <div className="flex items-center gap-3">
                 <span className="text-sm text-dark-text-secondary hidden md:inline">
                   {user?.name || user?.username || user?.email || 'Usuário'}
                 </span>
                 <button
                   onClick={async () => { await logout(); navigate('/login'); }}
                   className="px-3 py-1 rounded-md text-sm font-medium bg-gray-700 hover:bg-gray-600 text-white"
                 >
                   Sair
                 </button>
               </div>
             )}
           </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
